import React, { useEffect } from 'react'
import { Container, Row, Col, Card, Badge } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getDashboardMe, getFollowUp, getInvoicesAll, selectStudent } from '../../actions/usersActions'

const StatTile = ({ label, value, valueColor, tileBg }) => (
    <div className={`rounded-3 p-3 text-center h-100 ${tileBg || 'bg-light'}`}>
        <p className='text-muted mb-1' style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{label}</p>
        <p className='mb-0 fw-bold' style={{ fontSize: '22px', color: valueColor || '#000' }}>{value}</p>
    </div>
)

const SummarySkeleton = () => (
    <Row className='g-3 mb-4 dashboard-summary-row'>
        {[1, 2, 3].map(i => (
            <Col xs={12} md={4} key={i}>
                <Card className='dashboard-summary-card h-100'>
                    <Card.Body className='d-flex p-4 justify-content-between align-items-center'>
                        <div>
                            <div className='bg-light rounded-3 mb-2' style={{ height: '12px', width: '80px' }} />
                            <div className='bg-light rounded-3' style={{ height: '24px', width: '100px' }} />
                        </div>
                        <div className="icon-circle bg-light">
                            <div className='bg-light rounded-circle' style={{ width: '40px', height: '40px' }} />
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        ))}
    </Row>
)

const StudentCardSkeleton = () => (
    <Card className='rounded-4 border-0 shadow-sm mb-3'>
        <Card.Body className='p-4'>
            <Row className='g-3 align-items-center'>
                <Col xs={12} lg={3}>
                    <div className='d-flex align-items-center gap-3'>
                        <div className='bg-light rounded-circle flex-shrink-0' style={{ width: '48px', height: '48px' }} />
                        <div className='flex-grow-1'>
                            <div className='bg-light rounded-3' style={{ height: '14px', width: '70%' }} />
                            <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '50%' }} />
                        </div>
                    </div>
                </Col>
                {[1, 2, 3, 4].map(j => (
                    <Col key={j} xs={6} lg={2}>
                        <div className='bg-light rounded-3' style={{ height: '64px', width: '100%' }} />
                    </Col>
                ))}
                <Col xs={12} lg={1}>
                    <div className='bg-light rounded-3' style={{ height: '34px', width: '100%' }} />
                </Col>
            </Row>
        </Card.Body>
    </Card>
)

export default function FollowUp() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { dashboard, dashboardError, followUp, followUpError, invoicesByStudent, invoicesAllLoading, followUpLoading } = useSelector(state => state.users)

    const students = dashboard?.students || []
    const allLoaded = dashboard && !followUpLoading && !invoicesAllLoading

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
        if (!dashboard) dispatch(getDashboardMe())
        if (followUp.length === 0) dispatch(getFollowUp())
        if (Object.keys(invoicesByStudent).length === 0) dispatch(getInvoicesAll())
    }, [dispatch, dashboard])

    const isPaid = (inv) => inv.paymentState === 'paid' || inv.state === 'posted'
    const invoiceYear = (inv) => {
        const d = inv.invoiceDate || inv.date
        return d ? new Date(d).getFullYear() : null
    }

    const summary = students.reduce((acc, s) => {
        const invoices = invoicesByStudent[s.id]?.invoices || []
        invoices.forEach(inv => {
            const year = invoiceYear(inv)
            if (year === new Date().getFullYear()) acc.yearTotal += inv.total || 0
            if (isPaid(inv)) acc.paidTotal += inv.total || 0
            else {
                acc.pendingTotal += inv.total || 0
                acc.pendingCount += 1
            }
        })
        return acc
    }, { pendingTotal: 0, paidTotal: 0, yearTotal: 0, pendingCount: 0 })

    const formatCurrency = (amount) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0)

    const getFollowUpData = (studentId) => followUp.find(f => String(f.studentId) === String(studentId)) || null

    const goTo = (studentId, path) => {
        dispatch(selectStudent(studentId))
        navigate(`/parent/${path}`)
    }

    const renderContent = () => {
        if (!allLoaded) {
            return (
                <>
                    <SummarySkeleton />
                    <h5 className='fw-bold text-black mb-3'>{t('parent.detailbystudent')}</h5>
                    <StudentCardSkeleton />
                </>
            )
        }

        return (
            <>
                <Row className='g-3 mb-4 dashboard-summary-row'>
                    <Col xs={12} md={4}>
                        <Card className='dashboard-summary-card h-100'>
                            <Card.Body className='d-flex p-4 justify-content-between align-items-center'>
                                <div>
                                    <p className='my-0 text-muted' style={{ fontSize: '14px' }}>{t('parent.totalpending')}</p>
                                    <h4 className='fw-bold text-black mb-0'>{formatCurrency(summary.pendingTotal)}</h4>
                                </div>
                                <div className="icon-circle bg-danger bg-opacity-10">
                                    <img src="/images/exclamation.svg" alt="pendiente" />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} md={4}>
                        <Card className='dashboard-summary-card h-100'>
                            <Card.Body className='d-flex p-4 justify-content-between align-items-center'>
                                <div>
                                    <p className='my-0 text-muted' style={{ fontSize: '14px' }}>{t('parent.totalpaid')}</p>
                                    <h4 className='fw-bold text-black mb-0'>{formatCurrency(summary.paidTotal)}</h4>
                                </div>
                                <div className="icon-circle bg-success bg-opacity-10">
                                    <img src="/images/check-dashboard.svg" alt="pagado" />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} md={4}>
                        <Card className='dashboard-summary-card h-100'>
                            <Card.Body className='d-flex p-4 justify-content-between align-items-center'>
                                <div>
                                    <p className='my-0 text-muted' style={{ fontSize: '14px' }}>{t('parent.totalyear')}</p>
                                    <h4 className='fw-bold text-black mb-0'>{formatCurrency(summary.yearTotal)}</h4>
                                </div>
                                <div className="icon-circle bg-primary bg-opacity-10">
                                    <img src="/images/creditcard.svg" alt="total" />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <h5 className='fw-bold text-black mb-3'>{t('parent.detailbystudent')}</h5>

                {students.map(student => {
                    const fu = getFollowUpData(student.id)
                    const invoices = invoicesByStudent[student.id]?.invoices || []
                    const pendingInvoices = invoices.filter(inv => !isPaid(inv))
                    const hasPending = pendingInvoices.length > 0

                    return (
                        <Card key={student.id} className='rounded-4 border-0 shadow-sm mb-3'>
                            <Card.Body className='p-4'>
                                <Row className='g-3 align-items-center'>
                                    <Col xs={12} lg={3}>
                                        <div className='d-flex align-items-center gap-3'>
                                            <div className='rounded-circle bg-light d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden'
                                                style={{ width: '48px', height: '48px', fontSize: '15px', fontWeight: 700, color: '#6c757d' }}>
                                                {student.avatar
                                                    ? <img src={student.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    : (student.name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('')
                                                }
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <p className='mb-1 fw-bold text-black text-truncate'>{student.name}</p>
                                                <p className='mb-1 text-muted text-truncate' style={{ fontSize: '13px' }}>{student.courseName || '—'}</p>
                                                <div className='d-flex gap-1 flex-wrap'>
                                                    {student.group && <Badge bg='light' text='dark' className='border'>{student.group}</Badge>}
                                                    <Badge bg={student.active ? 'success-subtle' : 'light'} text={student.active ? 'success' : 'dark'} className='border'>
                                                        {student.active ? t('users.active') : t('users.inactive')}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={6} lg={2}>
                                        <StatTile label={t('parent.attendance')}
                                            value={fu?.attendance?.percentage !== null && fu?.attendance?.percentage !== undefined ? `${fu.attendance.percentage}%` : '—'}
                                            valueColor='#f59e0b' />
                                    </Col>
                                    <Col xs={6} lg={2}>
                                        <StatTile label={t('parent.averagegrade')}
                                            value={fu?.averageGrade !== null && fu?.averageGrade !== undefined ? fu.averageGrade.toFixed(1) : '—'}
                                            valueColor='#2563eb' />
                                    </Col>
                                    <Col xs={6} lg={2}>
                                        <StatTile label={t('parent.pendingtasks')} value='—' />
                                    </Col>
                                    <Col xs={6} lg={2}>
                                        <StatTile label={t('parent.pendinginvoices')}
                                            value={pendingInvoices.length}
                                            valueColor={hasPending ? '#b45309' : '#15803d'}
                                            tileBg={hasPending ? 'bg-warning bg-opacity-10' : 'bg-success bg-opacity-10'} />
                                    </Col>
                                    <Col xs={12} lg={1} className='d-flex flex-lg-column gap-2 justify-content-center'>
                                        <button onClick={() => goTo(student.id, 'academic')}
                                            className='btn border rounded-3 d-flex align-items-center justify-content-center gap-1 w-100'
                                            style={{ fontSize: '13px', padding: '8px 10px', color: '#374151', borderColor: '#d1d5db', backgroundColor: 'transparent' }}>
                                            <img src="/images/openbook.svg" alt="" width={14} />
                                            <span className='d-lg-none'>{t('parent.viewacademic')}</span>
                                        </button>
                                        <button onClick={() => goTo(student.id, 'administration')}
                                            className='btn border rounded-3 d-flex align-items-center justify-content-center gap-1 w-100'
                                            style={{ fontSize: '13px', padding: '8px 10px', color: '#374151', borderColor: '#d1d5db', backgroundColor: 'transparent' }}>
                                            <img src="/images/document.svg" alt="" width={14} />
                                            <span className='d-lg-none'>{t('parent.viewfinancial')}</span>
                                        </button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )
                })}
            </>
        )
    }

    return (
        <Container className='my-3 p-5'>
            <Row className='align-items-center mb-4'>
                <Col>
                    <h1 className='fw-bolder text-black mb-1'>{t('admin.dashboard.followup')}</h1>
                    <p className='text-muted mb-0'>{t('parent.followupdesc')}</p>
                </Col>
                <Col xs='auto'>
                    <div className='d-flex align-items-center gap-2 text-muted'>
                        <img src="/images/users.svg" alt="alumnos" width={18} />
                        {!allLoaded
                            ? <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                            : <span className='fw-semibold'>{students.length} {t('parent.studentscount')}</span>}
                    </div>
                </Col>
            </Row>

            {followUpError && (
                <Row className='mb-3'>
                    <Col>
                        <Card className='border-0'>
                            <Card.Body className='bg-danger bg-opacity-10 rounded-3'>
                                <p className='mb-0 text-danger'><img src="/images/x-icon.svg" alt="x" width={18} className='me-2' />{t('users.odooUnreachable')}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {renderContent()}

            <Card className='rounded-4 border-0 mt-4 bg-primary bg-opacity-10'>
                <Card.Body className='p-4'>
                    <div className='d-flex align-items-center gap-2 mb-3'>
                        <img src="/images/exclamation.svg" alt="consejos" width={18} />
                        <p className='text-black fw-bold mb-0'>{t('parent.tips')}</p>
                    </div>
                    {['tip1', 'tip2', 'tip3', 'tip4'].map(key => (
                        <div key={key} className='d-flex align-items-start gap-2 mb-2'>
                            <span className='rounded-circle flex-shrink-0 mt-2' style={{ width: '5px', height: '5px', backgroundColor: '#374151' }} />
                            <p className='mb-0' style={{ fontSize: '14px', color: '#374151' }}>{t(`parent.${key}`)}</p>
                        </div>
                    ))}
                </Card.Body>
            </Card>
        </Container>
    )
}
